import { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, API_URL, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Coins, Plus, Minus, Save, Download, Settings as SettingsIcon, User, Clock, X, Check, XCircle, Copy, CreditCard, Trash2, Shield, AlertTriangle } from "lucide-react";

const PACK_OPTIONS = [
  { key: "pack_100", credits: 100, price: 30 },
  { key: "pack_200", credits: 200, price: 50 },
  { key: "pack_500", credits: 500, price: 100 },
  { key: "pack_1000", credits: 1000, price: 150 },
  { key: "pack_2000", credits: 2000, price: 200 },
  { key: "custom", credits: 0, price: 0 },
];

export default function Admin() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [confirmations, setConfirmations] = useState([]);
  const [platform, setPlatform] = useState(null);
  const [grantUser, setGrantUser] = useState(null);
  const [grantMode, setGrantMode] = useState("add"); // "add" | "revoke"
  const [grantForm, setGrantForm] = useState({ pack_key: "pack_100", credits: 100, amount: 30, payment_method: "paypal", note: "" });
  const [revokeForm, setRevokeForm] = useState({ credits: 10, reason: "" });
  const [busy, setBusy] = useState(false);
  const [validatingCnf, setValidatingCnf] = useState(null);

  const fetchUsers = () => {
    api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(({ data }) => setUsers(data)).catch(() => {});
  };
  const fetchConfirmations = () => {
    api.get("/admin/payment-confirmations?status=pending").then(({ data }) => setConfirmations(data)).catch(() => {});
  };

  useEffect(() => {
    fetchUsers();
    fetchConfirmations();
    api.get("/admin/platform-settings").then(({ data }) => setPlatform(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchUsers, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const openGrant = (u, preset, mode = "add") => {
    setGrantUser(u);
    setGrantMode(mode);
    setRevokeForm({ credits: 10, reason: "" });
    if (preset) {
      const p = PACK_OPTIONS.find((x) => x.key === preset.pack_key) || PACK_OPTIONS[0];
      setGrantForm({
        pack_key: p.key,
        credits: preset.credits || p.credits,
        amount: (preset.amount_cents || p.price * 100) / 100,
        payment_method: "paypal",
        note: preset.note || "",
      });
    } else {
      setGrantForm({ pack_key: "pack_100", credits: 100, amount: 30, payment_method: "paypal", note: "" });
    }
  };

  const onPackChange = (v) => {
    const p = PACK_OPTIONS.find((x) => x.key === v);
    if (p && p.key !== "custom") {
      setGrantForm({ ...grantForm, pack_key: v, credits: p.credits, amount: p.price });
    } else {
      setGrantForm({ ...grantForm, pack_key: "custom" });
    }
  };

  const submitGrant = async () => {
    if (!grantUser) return;
    setBusy(true);
    try {
      if (grantMode === "revoke") {
        const { data } = await api.post("/admin/credits/revoke", {
          user_id: grantUser.id,
          credits: Number(revokeForm.credits),
          reason: revokeForm.reason.trim(),
        });
        toast.success(`−${data.credits_revoked} crédits retirés · Solde restant : ${data.remaining_balance}`);
      } else {
        const { data } = await api.post("/admin/credits/grant", {
          user_id: grantUser.id,
          credits: Number(grantForm.credits),
          amount_cents: Math.round(Number(grantForm.amount) * 100),
          pack_key: grantForm.pack_key === "custom" ? null : grantForm.pack_key,
          payment_method: grantForm.payment_method,
          note: grantForm.note,
        });
        toast.success(`+${data.credits} crédits accordés · Facture ${data.invoice_number}`);
      }
      setGrantUser(null);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const savePlatform = async () => {
    try {
      const { data } = await api.put("/admin/platform-settings", platform);
      setPlatform(data);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadInvoice = async (userId, invoiceId, number) => {
    try {
      const token = localStorage.getItem("vape_token");
      const resp = await fetch(`${API_URL}/invoices/${invoiceId}/pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `facture_${number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const totalCredits = useMemo(() => users.reduce((s, u) => s + (u.credits || 0), 0), [users]);
  const totalLabels = useMemo(() => users.reduce((s, u) => s + (u.labels_printed || 0), 0), [users]);

  const validateConfirmation = async (cnfId) => {
    setValidatingCnf(cnfId);
    try {
      const { data } = await api.post(`/admin/payment-confirmations/${cnfId}/validate`);
      toast.success(`+${data.credits_granted} crédits activés · Facture ${data.invoice_number} · Email client envoyé`);
      fetchConfirmations();
      fetchUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setValidatingCnf(null);
    }
  };

  const rejectConfirmation = async (cnfId) => {
    if (!window.confirm("Rejeter cette confirmation ? Le client sera notifié.")) return;
    setValidatingCnf(cnfId);
    try {
      await api.post(`/admin/payment-confirmations/${cnfId}/reject`);
      toast.success("Confirmation rejetée");
      fetchConfirmations();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setValidatingCnf(null);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success("Code copié"));
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.email} et toutes ses données (liquides, historique, fiches, crédits) ?\n\nLes factures sont conservées pour la comptabilité.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.email} supprimé`);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout title="Administration">
      {/* Tabs */}
      <div className="mb-6 inline-flex border border-zinc-800 flex-wrap">
        {[
          { k: "users", label: `Clients (${users.length})` },
          { k: "confirmations", label: `À valider (${confirmations.length})`, badge: confirmations.length > 0 },
          { k: "platform", label: "Paramètres plateforme" },
        ].map(({ k, label, badge }) => (
          <button
            key={k}
            data-testid={`admin-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-5 py-2.5 text-sm font-medium border-r last:border-r-0 border-zinc-800 transition-colors relative ${
              tab === k ? "bg-red-500 text-white" : "bg-zinc-950 text-zinc-400 hover:text-white"
            }`}
          >
            {label}
            {badge && tab !== k && <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-12 gap-0 border border-zinc-800 mb-6">
            <Stat label="Clients inscrits" value={users.length} accent="red" />
            <Stat label="Crédits en circulation" value={totalCredits.toLocaleString("fr-FR")} accent="green" />
            <Stat label="Étiquettes imprimées" value={totalLabels.toLocaleString("fr-FR")} accent="white" />
          </div>

          {/* Search */}
          <div className="mb-4 relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher par email ou nom de boutique…"
              className="rounded-none bg-zinc-900 border-zinc-800 pl-9"
            />
          </div>

          {/* Users table */}
          <div className="border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 border-b border-zinc-800">
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                  <th className="px-4 py-3">Boutique</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Inscrit</th>
                  <th className="px-4 py-3 text-right">Étiquettes</th>
                  <th className="px-4 py-3 text-right">Solde</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500">Aucun client trouvé.</td></tr>
                )}
                {users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-zinc-800 hover:bg-zinc-900/60">
                    <td className="px-4 py-3 font-semibold text-white">
                      <div className="flex items-center gap-2">
                        {u.shop_name || "—"}
                        {u.role === "admin" && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest bg-red-500/20 border border-red-500/40 text-red-400">
                            <Shield className="w-2.5 h-2.5" /> Admin
                          </span>
                        )}
                        {u.is_self && (
                          <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">(vous)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-400">{u.email}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-right text-zinc-300">{u.labels_printed}</td>
                    <td className="px-4 py-3 font-mono text-sm text-right">
                      <span className={u.credits > 0 ? "text-green-400 font-bold" : "text-zinc-600"}>{u.role === "admin" ? "∞" : u.credits}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {u.role !== "admin" && (
                          <>
                            <Button
                              data-testid={`admin-grant-btn-${u.id}`}
                              onClick={() => openGrant(u, null, "add")}
                              size="sm"
                              className="bg-red-500 hover:bg-red-600 rounded-none"
                            >
                              <Plus className="w-3.5 h-3.5 mr-1" /> Créditer
                            </Button>
                            {u.credits > 0 && (
                              <Button
                                data-testid={`admin-revoke-btn-${u.id}`}
                                onClick={() => openGrant(u, null, "revoke")}
                                size="sm"
                                variant="outline"
                                className="rounded-none border-zinc-700 bg-transparent text-orange-400 hover:text-orange-300 hover:border-orange-400"
                                title="Retirer des crédits"
                              >
                                <Minus className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </>
                        )}
                        {!u.is_self && (
                          <Button
                            data-testid={`admin-delete-btn-${u.id}`}
                            onClick={() => deleteUser(u)}
                            size="sm"
                            variant="outline"
                            className="rounded-none border-zinc-700 bg-transparent text-zinc-400 hover:text-red-400 hover:border-red-400 px-2"
                            title="Supprimer ce compte"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "confirmations" && (
        <div className="border border-zinc-800">
          <div className="px-6 py-4 border-b border-zinc-800 text-sm text-zinc-400">
            {confirmations.length === 0
              ? "Aucun paiement en attente de validation. Quand un client saisira son code PayPal, il apparaîtra ici."
              : `${confirmations.length} paiement(s) à vérifier — vérifiez le code sur votre compte PayPal, puis validez.`}
          </div>
          {confirmations.map((c) => (
            <div key={c.id} data-testid={`cnf-row-${c.id}`} className="px-6 py-5 border-b border-zinc-800 last:border-b-0 hover:bg-zinc-900/40">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="font-semibold text-white">{c.shop_name}</div>
                    <div className="text-xs font-mono text-zinc-500">{c.user_email}</div>
                    <span className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-red-500 text-white">
                      {c.credits} crédits · {(c.amount_cents / 100).toFixed(2)}€
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Code PayPal :</span>
                    <button
                      data-testid={`cnf-code-${c.id}`}
                      onClick={() => copyCode(c.confirmation_code)}
                      className="font-mono text-sm text-yellow-400 bg-yellow-500/5 border border-yellow-500/30 px-2 py-1 hover:bg-yellow-500/10 flex items-center gap-1.5"
                    >
                      {c.confirmation_code}
                      <Copy className="w-3 h-3" />
                    </button>
                    <a
                      href="https://www.paypal.com/myaccount/activity"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] font-mono text-blue-400 hover:text-blue-300 underline"
                    >
                      Vérifier sur PayPal →
                    </a>
                  </div>
                  <div className="text-[10px] text-zinc-500 font-mono mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Reçu {c.created_at ? new Date(c.created_at).toLocaleString("fr-FR") : ""}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button
                    data-testid={`cnf-validate-${c.id}`}
                    onClick={() => validateConfirmation(c.id)}
                    disabled={validatingCnf === c.id}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 rounded-none"
                  >
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    {validatingCnf === c.id ? "…" : "Valider"}
                  </Button>
                  <Button
                    data-testid={`cnf-reject-${c.id}`}
                    onClick={() => rejectConfirmation(c.id)}
                    disabled={validatingCnf === c.id}
                    size="sm"
                    variant="outline"
                    className="rounded-none border-zinc-700 bg-transparent text-zinc-400 hover:text-red-400 hover:border-red-400"
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1.5" /> Rejeter
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "platform" && platform && (
        <div className="max-w-2xl border border-zinc-800 p-6 space-y-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-500 mb-2">Paramètres de la plateforme</div>
          <Field label="Identifiant PayPal.me" testid="platform-paypal">
            <div className="flex items-center gap-2">
              <span className="text-zinc-500 font-mono text-sm">paypal.me/</span>
              <Input value={platform.paypal_me_handle || ""} onChange={(e) => setPlatform({ ...platform, paypal_me_handle: e.target.value })} placeholder="mixovap" className="rounded-none bg-zinc-900 border-zinc-800" data-testid="platform-paypal-input" />
            </div>
            <div className="text-[10px] text-zinc-500 mt-1 font-mono">
              Tapez uniquement votre identifiant (ex: <span className="text-white">mixovap</span>), sans "paypal.me/" ni "https://". Créez votre lien sur paypal.me si vous n'en avez pas.
            </div>
          </Field>
          <Field label="Email de contact (pour les clients qui écrivent)" testid="platform-email">
            <Input value={platform.contact_email || ""} onChange={(e) => setPlatform({ ...platform, contact_email: e.target.value })} placeholder="contact@mixovap.fr" className="rounded-none bg-zinc-900 border-zinc-800" data-testid="platform-email-input" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nom de l'entreprise (facture)" testid="platform-company">
              <Input value={platform.company_name || ""} onChange={(e) => setPlatform({ ...platform, company_name: e.target.value })} className="rounded-none bg-zinc-900 border-zinc-800" data-testid="platform-company-input" />
            </Field>
            <Field label="SIRET" testid="platform-siret">
              <Input value={platform.company_siret || ""} onChange={(e) => setPlatform({ ...platform, company_siret: e.target.value })} className="rounded-none bg-zinc-900 border-zinc-800 font-mono" data-testid="platform-siret-input" />
            </Field>
          </div>
          <Field label="Adresse (facture)" testid="platform-addr">
            <Input value={platform.company_address || ""} onChange={(e) => setPlatform({ ...platform, company_address: e.target.value })} className="rounded-none bg-zinc-900 border-zinc-800" data-testid="platform-addr-input" />
          </Field>
          <Field label="N° TVA intracommunautaire" testid="platform-tva">
            <Input value={platform.company_tva || ""} onChange={(e) => setPlatform({ ...platform, company_tva: e.target.value })} placeholder="FRXX999999999" className="rounded-none bg-zinc-900 border-zinc-800 font-mono" data-testid="platform-tva-input" />
          </Field>
          <Button data-testid="platform-save-btn" onClick={savePlatform} className="bg-red-500 hover:bg-red-600 rounded-none">
            <Save className="w-4 h-4 mr-2" /> Enregistrer
          </Button>
        </div>
      )}

      {/* Grant/Revoke credits modal */}
      {grantUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setGrantUser(null)}>
          <div className="bg-zinc-950 border border-zinc-800 w-full max-w-md mx-4 relative" onClick={(e) => e.stopPropagation()}>
            <button data-testid="grant-modal-close" onClick={() => setGrantUser(null)} className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            <div className="p-6 border-b border-zinc-800">
              <div className={`font-mono text-[10px] uppercase tracking-[0.3em] mb-2 ${grantMode === "revoke" ? "text-orange-400" : "text-red-500"}`}>
                {grantMode === "revoke" ? "Retirer des crédits" : "Créditer un client"}
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-zinc-500" />
                <span className="text-white font-semibold">{grantUser.shop_name}</span>
                <span className="text-xs font-mono text-zinc-500">— {grantUser.email}</span>
              </div>
            </div>

            {/* Mode toggle */}
            <div className="flex border-b border-zinc-800">
              <button
                data-testid="mode-add"
                onClick={() => setGrantMode("add")}
                className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
                  grantMode === "add" ? "bg-red-500 text-white" : "bg-zinc-950 text-zinc-500 hover:text-white"
                }`}
              >
                <Plus className="w-4 h-4" /> Ajouter
              </button>
              <button
                data-testid="mode-revoke"
                onClick={() => setGrantMode("revoke")}
                className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-l border-zinc-800 ${
                  grantMode === "revoke" ? "bg-orange-500 text-white" : "bg-zinc-950 text-zinc-500 hover:text-white"
                }`}
              >
                <Minus className="w-4 h-4" /> Retirer
              </button>
            </div>

            {grantMode === "add" ? (
              <div className="p-6 space-y-4">
                <Field label="Pack" testid="grant-pack">
                  <Select value={grantForm.pack_key} onValueChange={onPackChange}>
                    <SelectTrigger className="rounded-none bg-zinc-900 border-zinc-800" data-testid="grant-pack-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 rounded-none">
                      {PACK_OPTIONS.map((p) => (
                        <SelectItem key={p.key} value={p.key} data-testid={`grant-pack-${p.key}`}>
                          {p.key === "custom" ? "Montant personnalisé" : `${p.credits} crédits · ${p.price}€ TTC`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Crédits à ajouter" testid="grant-credits">
                    <Input type="number" min="1" value={grantForm.credits} onChange={(e) => setGrantForm({ ...grantForm, credits: e.target.value })} className="rounded-none bg-zinc-900 border-zinc-800 font-mono" data-testid="grant-credits-input" />
                  </Field>
                  <Field label="Montant TTC (€)" testid="grant-amount">
                    <Input type="number" step="0.01" min="0" value={grantForm.amount} onChange={(e) => setGrantForm({ ...grantForm, amount: e.target.value })} className="rounded-none bg-zinc-900 border-zinc-800 font-mono" data-testid="grant-amount-input" />
                  </Field>
                </div>
                <Field label="Mode de paiement" testid="grant-method">
                  <Select value={grantForm.payment_method} onValueChange={(v) => setGrantForm({ ...grantForm, payment_method: v })}>
                    <SelectTrigger className="rounded-none bg-zinc-900 border-zinc-800" data-testid="grant-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 rounded-none">
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="bank_transfer">Virement bancaire</SelectItem>
                      <SelectItem value="email">Autre (contact email)</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Note (optionnel)" testid="grant-note">
                  <Input value={grantForm.note} onChange={(e) => setGrantForm({ ...grantForm, note: e.target.value })} placeholder="Ex: Paiement PayPal du 15/09" className="rounded-none bg-zinc-900 border-zinc-800" data-testid="grant-note-input" />
                </Field>
                <Button data-testid="grant-submit-btn" onClick={submitGrant} disabled={busy || !grantForm.credits} className="w-full bg-red-500 hover:bg-red-600 rounded-none py-6 text-base">
                  <Coins className="w-4 h-4 mr-2" />
                  {busy ? "Création…" : `Créditer +${grantForm.credits} crédits & émettre facture`}
                </Button>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <div className="border-l-2 border-orange-500 bg-orange-500/5 pl-3 py-2 text-xs text-zinc-300">
                  <div className="font-semibold text-orange-400 mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Attention</div>
                  Utilisez cette action pour un chargeback PayPal, une fraude avérée, un paiement finalement annulé. L'opération est <b className="text-white">tracée dans l'historique</b> du client.
                  <div className="mt-2 font-mono text-[11px] text-zinc-400">Solde actuel : <b className="text-white">{grantUser.credits ?? 0}</b> crédits</div>
                </div>
                <Field label="Crédits à retirer" testid="revoke-credits">
                  <Input type="number" min="1" max={grantUser.credits ?? undefined} value={revokeForm.credits} onChange={(e) => setRevokeForm({ ...revokeForm, credits: e.target.value })} className="rounded-none bg-zinc-900 border-zinc-800 font-mono" data-testid="revoke-credits-input" />
                </Field>
                <Field label="Motif (obligatoire)" testid="revoke-reason">
                  <Input value={revokeForm.reason} onChange={(e) => setRevokeForm({ ...revokeForm, reason: e.target.value })} placeholder="Ex: Chargeback PayPal du 20/09, litige en cours…" className="rounded-none bg-zinc-900 border-zinc-800" data-testid="revoke-reason-input" />
                </Field>
                <Button
                  data-testid="revoke-submit-btn"
                  onClick={submitGrant}
                  disabled={busy || !revokeForm.credits || !revokeForm.reason.trim() || revokeForm.reason.trim().length < 3}
                  className="w-full bg-orange-500 hover:bg-orange-600 rounded-none py-6 text-base"
                >
                  <Minus className="w-4 h-4 mr-2" />
                  {busy ? "…" : `Retirer −${revokeForm.credits} crédits`}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const c = { red: "text-red-500", green: "text-green-400", white: "text-white" }[accent];
  return (
    <div className="col-span-12 md:col-span-4 p-6 border-b md:border-b-0 md:[&:not(:first-child)]:border-l border-zinc-800">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</div>
      <div className={`font-display text-4xl font-extrabold tracking-tighter mt-2 ${c}`}>{value}</div>
    </div>
  );
}
